import ArrowLeft from "@svg/new-design/arrow-left";
import { FC, useMemo } from "react";
import { festival, STATUS } from "@types";
import dynamic from "next/dynamic";
import { useLayoutContext } from "@context/layout";
import { useIsMobile } from "@shared/hooks/useIsMobile";
import { CURRENT_STATUS } from "@shared/components/card/types";
import BoughtFooterCard from "./BoughtFooterCard";
import CommonFooterCard from "./CommonFooterCard";
import {
  BUTTON_COLOR,
  BUTTON_SIZE,
  BUTTON_VARIANT,
} from "@shared/components/button";

const Button = dynamic(() => import("@shared/components/button/Button"), {
  ssr: false,
});

const publishSorting = [
  STATUS.PLANNING,
  STATUS.START_RECORDING,
  STATUS.RECORDING,
  STATUS.EDITING,
  STATUS.QUALITY_INSPECTION,
  STATUS.PUBLISHING,
];

interface Props {
  isBuy: boolean;
  status: STATUS;
  price: number | undefined;
  watchPercent: number;
  // eslint-disable-next-line no-unused-vars
  submitRate?: ((id: number) => void) | undefined;
  id: number;
  festival: festival | undefined;
  showShoppingCart: boolean;
  inCart: boolean | undefined;
  sku: string;
  hasCertificate?: boolean;
}

const CardFooter: FC<Props> = (props) => {
  const {
    isBuy,
    status,
    price,
    watchPercent,
    festival,
    submitRate,
    id,
    sku,
    inCart,
    hasCertificate,
  } = props;
  const isMobile = useIsMobile();
  const salePrice = festival?.price_on_festival;
  const { subscriptionData } = useLayoutContext();
  const hasActiveSubscription = Boolean(subscriptionData?.is_active);

  const isPercentDiscount = festival?.is_percent;
  const discount = hasActiveSubscription ? undefined : festival?.discount_value;
  const boughtLabel = watchPercent
    ? watchPercent === 100
      ? hasCertificate
        ? isMobile
          ? "گواهینامه"
          : "دریافت گواهینامه"
        : "امتیاز شما؟"
      : "ادامه یادگیری"
    : "شروع یادگیری";

  const currentStatus = useMemo(() => {
    if (status !== STATUS.PUBLISHED)
      return CURRENT_STATUS.NOT_PUBLISH; // not published
    else if (isBuy)
      return CURRENT_STATUS.BOUGHT; // bought before
    else if (price === 0)
      return CURRENT_STATUS.FREE; // free
    else return CURRENT_STATUS.DEFAULT; // show price (released, not free, not bought)
  }, [isBuy, price, status]);

  const footerComponent = {
    [CURRENT_STATUS.NOT_PUBLISH]: (
      <div className="flex  items-center h-10">
        <p className="text-fds-brand button-sm">
          {publishSorting.findIndex((pItm) => pItm === status) <= 2
            ? "در فرایند انتشار"
            : "به زودی"}
        </p>
      </div>
    ),
    [CURRENT_STATUS.BOUGHT]: (
      <BoughtFooterCard
        watchPercent={watchPercent}
        boughtLabel={boughtLabel}
        hasCertificate={hasCertificate}
      />
    ),
    [CURRENT_STATUS.FREE]: (
      <div className="flex justify-between items-center h-fds-42">
        <p className="sm:body-md body-sm text-fds-secondary">رایگان</p>
        <Button
          onClick={() =>
            watchPercent === 100 ? submitRate?.(id || 0) : undefined
          }
          variant={BUTTON_VARIANT.TEXT}
          color={BUTTON_COLOR.PRIMARY}
          endIcon={<ArrowLeft className="h-5 w-5  " />}
          size={BUTTON_SIZE.MEDIUM}
        >
          {isMobile ? "شروع یادگیری" : "یادگیری را شروع کنید"}
        </Button>
      </div>
    ),
    [CURRENT_STATUS.DEFAULT]: (
      <CommonFooterCard
        salePrice={salePrice}
        price={price}
        discount={discount}
        isPercentDiscount={isPercentDiscount}
        id={id}
        sku={sku}
        inCart={inCart}
      />
    ),
  };

  return <div className={"w-full  mt-2"}>{footerComponent[currentStatus]}</div>;
};

export default CardFooter;
